<template>
  <div class="step3 step" :class="activeClass">
    <div class="font-medium text-xl lg1024:text-3xl">
      {{ $t('REGISTRATION_CITY') }}
    </div>

    <!-- Delivery / City Selection -->
    <div
      class="stepContent"
      tabindex="0"
      @keyup.enter="choosePositionFromPositionModal"
    >
      <div class="city-block w-full">
        <p class="mt-4">{{ $t('choose_delivery_city_desc') }}</p>

        <div v-if="hasApiError" class="mt-12 text-red-500">
          <div v-for="(error, index) in apiErrors" :key="index">
            {{ getError(error) }}
          </div>
        </div>

        <div class="flex flex-col mt-6 mb-2 w-full">
          <label class="flex" for="position-input__modal-window">
            {{ $t('delivery_city1') }}<span class="required">*</span>
          </label>

          <div
            class="autocomplete-wrapper relative mt-1"
            :class="{ 'city-field-locked': cityFieldLocked }"
          >
            <GMapAutocomplete
              id="position-input__modal-window"
              :disabled="isGmapLoading || cityFieldLocked"
              ref="gmapInput"
              class="input-wrap w-full border border-gray-2 p-3"
              data-qa="step2-city-autocomplete"
              :placeholder="''"
              :types="autocompleteTypes"
              :componentRestrictions="{ country: countryRestriction }"
              :fields="[
                'address_components',
                'formatted_address',
                'geometry',
                'place_id',
              ]"
              :value="initialCityValue"
              @input="handleInputChange"
              @focusout="checkPosition($event.target.value)"
              @place_changed="handlePlaceChanged($event, false, 'cityField')"
            />
            <LoaderAnimate1 v-if="isGmapLoading" class="autocomplete-loader" />
          </div>
          <div v-if="!isAdvancedCountry">
            <p class="text-gray-3 text-sm mt-1">
              <span v-if="isCitySearch"
                >{{ $t('advanced_search_use_1') }}
                <a
                  class="block sm640:inline cursor-pointer text-red-600 underline decoration-dashed"
                  @click="isCitySearch = !isCitySearch"
                  data-qa="step2-advanced-search-toggle"
                  >{{ $t('advanced_search_use_2') }}</a
                >
              </span>
              <span v-else
                >{{ $t('advanced_search_by_city_1') }}
                <a
                  class="block sm640:inline cursor-pointer text-red-600 underline decoration-dashed"
                  @click="isCitySearch = !isCitySearch"
                  data-qa="step2-advanced-search-toggle"
                  >{{ $t('advanced_search_by_city_2') }}</a
                >
              </span>
            </p>
          </div>

          <Skeletor
            v-if="isGmapLoading"
            class="flex"
            :height="20"
            :width="'100%'"
          />
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, watchEffect } from 'vue'
import { useStore } from 'vuex'
// import useDeliveryStep2 from './composables/useDeliveryStep2'
import { getError } from '@/utils/error'
import { choosePlace } from '@/utils/googlePlaces'
import { isCityInCrimeaSync } from '@/utils/crimea'
import LoaderAnimate1 from '@/components/svg/LoaderAnimate1.vue'
// Пропсы
const props = defineProps({
  step: Number,
  loading: Object,
  country: String,
  positionData: { type: Object, required: true },
  countryRestriction: [String, Array],
  cityFieldLocked: { type: Boolean, default: false },

  choosePositionFromPositionModal: { type: Function, required: true },
  formPositionData: { type: Function, required: true },
  checkPosition: { type: Function, required: true },
})

const countriesAdvancedSearch = ref(['US'])
const isAdvancedCountry = computed(() =>
  countriesAdvancedSearch.value.includes(props.country)
)

const isCitySearch = ref(true)
const autocompleteTypes = computed(() =>
  isAdvancedCountry.value || !isCitySearch.value ? [] : ['(cities)']
)

// ——— Стор для SELECTED_PROFILE ———
const store = useStore()
const apiErrors = computed(() => store.getters.apiErrors)
const hasApiError = computed(() => store.getters.hasApiError)
const rawLast = computed(
  () => store.state.SELECTED_PROFILE?.ADDRESS_INFO_FIELDS || {}
)
const hasLastProfile = computed(() => Object.keys(rawLast.value).length > 0)

// ——— Актуальный query пользователя ———
const currentQuery = ref('')

// ——— initialCityValue: пропсы в приоритете, иначе SELECTED_PROFILE ———
// Для Крыма не передаём AREA в поисковом запросе
const initialCityValue = computed(() => {
  const { CITY_NAME, AREA_NAME } = props.positionData || {}
  // если в props заданы оба поля — возвращаем их (но для Крыма без AREA)
  if (CITY_NAME && AREA_NAME) {
    return isCityInCrimeaSync(CITY_NAME)
      ? CITY_NAME
      : `${CITY_NAME}, ${AREA_NAME}`
  }
  // иначе пытаемся взять из хранилища
  if (hasLastProfile.value) {
    // Для Крыма тоже не передаём AREA
    if (isCityInCrimeaSync(rawLast.value.CITY)) {
      return rawLast.value.CITY || ''
    }
    return [rawLast.value.CITY, rawLast.value.AREA].filter(Boolean).join(', ')
  }
  return ''
})

// Пропускаем далее
const activeClass = computed(() => {
  return {
    unactive: props.step < 3,
    active: props.step >= 3,
  }
})
const gmapInput = ref(null)
const isGmapLoading = ref(false)

// const { choosePositionFromPositionModal, formPositionData, checkPosition } =
// useDeliveryStep2({ emit, gmapInput })

function editScenario() {
  store.commit('setCheckoutScenario', 'userEditProfile')
}

// ————————————————
//  Обёртка для formPositionData с проверкой missing fields
// ————————————————
function handlePlaceChanged(googleObject, withProfile, source) {
  if (!googleObject || !googleObject.address_components) {
    props.formPositionData(googleObject, withProfile, source)
    return
  }

  props.formPositionData(googleObject, withProfile, source)
}

// Счётчик версий запроса — защита от гонки: при смене города или
// ручном вводе пользователя устаревший ответ Google Places игнорируется.
let cityRequestId = 0

// Сохраняем текущее значение input как query для Sentry
function handleInputChange(event) {
  currentQuery.value = event.target?.value || ''
  // Инвалидируем in-flight запрос, чтобы его результат не перезаписал ввод
  cityRequestId++
  editScenario()
}

// ————————————————
//  Автоматический поиск и выбор первой подсказки
// ————————————————
async function chooseCityByGoogle() {
  const requestId = ++cityRequestId
  console.log('chooseCityByGoogle1', initialCityValue.value)
  // Сохраняем query для Sentry
  currentQuery.value = initialCityValue.value
  isGmapLoading.value = true
  try {
    await choosePlace({
      inputValue: initialCityValue.value,
      types: autocompleteTypes.value, // для США, например, все типы. Для остальных -- только 'cities'
      country: props.countryRestriction, // здесь может быть 'RO' или ['RO','MD']
      onResult: (place, withProfile) => {
        if (requestId !== cityRequestId) return
        handlePlaceChanged(place, withProfile)
      },
      options: {
        store,
        source: 'city',
      },
    })
  } catch (error) {
    console.error(error)
  } finally {
    if (requestId === cityRequestId) {
      isGmapLoading.value = false
    }
    console.log('chooseCityByGoogle2', isGmapLoading.value)
  }
}

const isSelectedProfile = computed(
  () => store.state.checkoutScenario === 'isSelectedProfile'
)
const isNewProfile = computed(
  () => store.state.checkoutScenario === 'isNewProfile'
)
// ——— Ждём загрузки Google Maps ———

watchEffect(() => {
  const city = initialCityValue.value
  if (!city) {
    isGmapLoading.value = false
    return
  }

  const waitForGoogle = () => {
    if (window.google?.maps?.places) {
      if ((isSelectedProfile.value || isNewProfile.value) && city)
        chooseCityByGoogle()
    } else {
      setTimeout(waitForGoogle, 1000)
    }
  }
  waitForGoogle()
})
</script>

<style lang="scss" scoped>
@keyframes breathe {
  0%,
  100% {
    opacity: 1;
  }
  50% {
    opacity: 0.5;
  }
}

.autocomplete-wrapper.city-field-locked {
  filter: blur(2px);
  transform: scale(0.99);
  pointer-events: none;
  animation: breathe 1.5s ease-in-out infinite;
  background-color: rgba(44, 44, 44, 0.05);
}
.autocomplete-loader {
  background: transparent;
  position: absolute;
  right: 1rem;
  top: 50%;
  transform: translateY(-50%);
  z-index: 1;
}
.autocomplete-wrapper .spinner {
  border: 4px solid rgba(0, 0, 0, 0.1);
  border-top: 4px solid #333;
  border-radius: 50%;
  width: 24px;
  height: 24px;
  animation: spin 1s linear infinite;
}
@keyframes spin {
  to {
    transform: rotate(360deg);
  }
}
</style>
